import type { CalendarEventType } from "../../../../types/calendar";
import type { CalendarEvent } from "../../../../types/transactions";
import {
  eventRangeText,
  findStaffConflict,
} from "../mobile/detail/detailModel";
import type { ServiceRow } from "./bookingTypes";
import { fmtTime, fromMinutes, serviceLength, toMinutes } from "./draftModel";

const range = (startMin: number, endMin: number) =>
  `${fmtTime(fromMinutes(startMin))} – ${fmtTime(fromMinutes(endMin))}`;

/** Who an existing calendar event is: the client, or the time block type. */
const eventWho = (e: CalendarEventType): string => {
  if (e.isTimeBlock) {
    return ("type" in e && e.type) || "a time block";
  }
  const a = e as CalendarEvent;
  if (a.firstName || a.lastName) {
    return `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
  }
  return e.title?.split(" - ")[1] || e.title || "another appointment";
};

/**
 * The booking drawer's one overlap warning (design handoff: a single
 * summary above Next, warning only): the first scheduled row whose provider
 * is busy, against the draft's other rows first, then the loaded calendar
 * events (`findStaffConflict`, shared with the mobile reschedule; the edited
 * appointment's own events never count). "Overlaps Ava Chen · 9:00 AM –
 * 10:30 AM", or null.
 */
export function draftOverlap(
  rows: ServiceRow[],
  events: CalendarEventType[],
  opts: {
    excludeTransactionId?: string;
    serviceName: (row: ServiceRow) => string;
  },
): string | null {
  for (const row of rows) {
    if (!row.salonServiceId || !row.employeeId || !row.date || !row.time) {
      continue;
    }
    const startMin = toMinutes(row.time);
    const endMin = startMin + serviceLength(row);

    const other = rows.find((o) => {
      if (o.uid === row.uid || !o.salonServiceId || !o.time) return false;
      if (o.employeeId !== row.employeeId || o.date !== row.date) return false;
      const s = toMinutes(o.time);
      return startMin < s + serviceLength(o) && s < endMin;
    });
    if (other) {
      const s = toMinutes(other.time);
      return `Overlaps ${opts.serviceName(other) || "another service"} · ${range(s, s + serviceLength(other))}`;
    }

    const busy = findStaffConflict(events, {
      staffId: row.employeeId,
      day: row.date,
      startMin,
      endMin,
      excludeTransactionId: opts.excludeTransactionId,
    });
    if (busy) {
      return `Overlaps ${eventWho(busy)} · ${eventRangeText(busy)}`;
    }
  }
  return null;
}
